import { cn } from "@/lib/utils";
import type { Category } from "@/types";
import { Input } from "@groot/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface InlineCategorySelectProps {
  value: number | null;
  categories: Category[];
  onSave: (categoryId: number | null) => void;
  className?: string;
  focused?: boolean;
  onFocus?: () => void;
  onBlurComplete?: () => void;
}

/**
 * Group categories into parent groups + ungrouped, optionally filtered by a
 * search term. Shared by the inline select and the add-transaction row picker.
 */
export function getGroupedCategories(categories: Category[], searchTerm = "") {
  // Always get all groups (never filter groups by search)
  const allGroups = categories.filter((cat) => cat.isGroup);

  // Filter only the actual category items (not groups)
  const allNonGroups = categories.filter((cat) => !cat.isGroup);
  const filteredNonGroups = searchTerm
    ? allNonGroups.filter((cat) => cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : allNonGroups;

  // Map children to their parent groups
  const childrenMap = filteredNonGroups.reduce(
    (acc, cat) => {
      if (cat.groupId) {
        if (!acc[cat.groupId]) {
          acc[cat.groupId] = [];
        }
        acc[cat.groupId].push(cat);
      }
      return acc;
    },
    {} as Record<number, Category[]>,
  );

  // Categories without a group
  const ungrouped = filteredNonGroups.filter((cat) => !cat.groupId);

  // Only show groups that have matching children
  const groupsWithMatches = searchTerm
    ? allGroups.filter((group) => childrenMap[group.id]?.length > 0)
    : allGroups;

  // Sort groups by sortOrder
  const sortedGroups = groupsWithMatches.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return {
    groupCategories: sortedGroups,
    ungroupedCategories: ungrouped,
    childCategoriesMap: childrenMap,
  };
}

export function InlineCategorySelect({
  value,
  categories,
  onSave,
  className,
  focused = false,
  onFocus,
  onBlurComplete,
}: InlineCategorySelectProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedCategory = categories.find((cat) => cat.id === value);

  // Auto-open when focused prop changes to true
  useEffect(() => {
    if (focused && !isEditing) {
      setIsEditing(true);
    }
  }, [focused, isEditing]);

  const handleSelect = (categoryId: string) => {
    const id = categoryId === "none" ? null : Number(categoryId);
    onSave(id);
    setIsEditing(false);
    setSearchTerm("");
    onBlurComplete?.();
  };

  // Filter and group categories based on search term
  const { groupCategories, ungroupedCategories, childCategoriesMap } = useMemo(
    () => getGroupedCategories(categories, searchTerm),
    [categories, searchTerm],
  );

  if (!isEditing) {
    return (
      <div
        className={cn(
          "min-w-0 px-2 py-1 -mx-2 -my-1 rounded cursor-text hover:bg-muted/50 flex items-center min-h-[28px]",
          className,
        )}
        onClick={() => {
          setIsEditing(true);
          onFocus?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsEditing(true);
            onFocus?.();
          }
        }}
      >
        {selectedCategory ? (
          <div className="flex min-w-0 items-center gap-1">
            <span className="min-w-0 truncate text-sm">{selectedCategory.name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </div>
    );
  }

  // Seamless edit (Actual-style): ghost preserves cell metrics, trigger overlays
  return (
    <div className="relative">
      <div
        className="px-2 py-1 -mx-2 -my-1 flex items-center min-h-[28px] invisible"
        aria-hidden="true"
      >
        <span className="text-sm">{selectedCategory ? selectedCategory.name : "—"}</span>
      </div>
      <Select
        value={value?.toString() || "none"}
        onValueChange={handleSelect}
        open={isEditing}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditing(false);
            setSearchTerm("");
            onBlurComplete?.();
          }
        }}
      >
        <SelectTrigger
          className={cn(
            // z-50: float above the sticky table header when editing the first row
            "absolute -inset-x-2 -inset-y-1 z-50 px-2 text-sm border-primary bg-background",
            className,
          )}
          style={{ height: "auto", width: "auto" }}
        >
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        {/* position="popper": anchor the panel to the trigger (floating-ui tracks
            scroll/resize). The default "item-aligned" strategy solves a fixed
            position from the selected item's rect, which jumps around inside the
            scrollable table container (see e2e/category-select-align.spec.ts). */}
        <SelectContent position="popper" align="start">
          {/* Search Input */}
          <div className="flex items-center border-b px-3 pb-2">
            <Search className="size-4 shrink-0 opacity-50 mr-2" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 w-full border-0 bg-transparent p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
              onKeyDown={(e) => {
                // Prevent the select from closing when typing
                e.stopPropagation();
              }}
            />
          </div>

          {/* Category List */}
          <div className="max-h-[300px] overflow-y-auto">
            <SelectItem value="none">
              <span className="text-muted-foreground">None</span>
            </SelectItem>

            {groupCategories.length === 0 && ungroupedCategories.length === 0 && searchTerm && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No categories found for "{searchTerm}"
              </div>
            )}

            {/* Ungrouped categories first */}
            {ungroupedCategories.map((category) => (
              <SelectItem key={category.id} value={category.id.toString()}>
                <div className="flex items-center gap-1.5">{category.name}</div>
              </SelectItem>
            ))}

            {/* Then grouped categories */}
            {groupCategories.map((group) => (
              <div key={group.id}>
                {/* Group header - styled but not selectable unless it's a selectable group */}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 mt-1">
                  <div className="flex items-center gap-1.5">{group.name}</div>
                </div>

                {/* Group children */}
                {childCategoriesMap[group.id]?.map((child) => (
                  <SelectItem key={child.id} value={child.id.toString()} className="pl-6">
                    <div className="flex items-center gap-1.5">{child.name}</div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
}
